import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { SocketContext } from '../context/SocketContext';
import { useToast } from '../context/ToastContext';
import { FocusableSection, FocusableButton, FocusableCard } from '../components/Focusable';
import { API_URL } from '../config';
import * as FaIcons from 'react-icons/fa';

const Container = styled.div`
  padding: 2rem;
  padding-left: ${props => props.$sidebar ? 'calc(150px + 2rem)' : '2rem'};
  min-height: 100vh;
  background: #fff;
  transition: padding-left 350ms;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: 70px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  color: #000;
  font-size: 2rem;
  margin: 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const NewMessageButtonWrapper = styled.div`
  position: absolute;
  right: 0;

  @media (max-width: 768px) {
    position: static;
  }
`;

const NewMessageButton = styled.button`
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  color: #fff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(99, 44, 228, 0.4);
  }
`;

const MessagesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 800px;
`;

const MessageCard = styled.div`
  background: ${props => props.$unread ? 'rgba(99, 44, 228, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 12px;
  padding: 1rem 1.25rem;
  border-left: 3px solid ${props => props.$sent ? '#27ae60' : '#632ce4'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(99, 44, 228, 0.08);
  }
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const MessageFrom = styled.div`
  font-weight: 600;
  color: #000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MessageDirection = styled.span`
  font-size: 0.75rem;
  color: ${props => props.$sent ? '#27ae60' : '#632ce4'};
  font-weight: normal;
`;

const MessageTime = styled.div`
  color: #888;
  font-size: 0.85rem;
`;

const MessageContent = styled.div`
  color: #333;
  font-size: 1rem;
  line-height: 1.4;
`;

const UnreadBadge = styled.span`
  background: #632ce4;
  color: #fff;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  margin-left: 0.5rem;
`;

const ReadBadge = styled.span`
  color: #27ae60;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
  opacity: 0.6;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const MessageFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #888;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ModalTitle = styled.h2`
  color: #000;
  margin: 0 0 1.5rem 0;
  font-size: 1.25rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  color: #333;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: #fff;
  color: #000;

  &:focus {
    outline: none;
    border-color: #632ce4;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  resize: none;
  height: 100px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #632ce4;
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 0.85rem;
  color: ${props => props.$over ? '#e74c3c' : '#888'};
  margin-top: 0.25rem;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;

  &.primary {
    background: linear-gradient(135deg, #632ce4, #9d50bb);
    color: #fff;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: #f0f0f0;
    color: #333;
  }
`;

export const Messages = ({ sidebar }) => {
  const { user } = useContext(AuthContext);
  const { socket } = useContext(SocketContext);
  const toast = useToast();

  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadMessages();
    loadContacts();
  }, []);

  // Écouter les nouveaux messages (mise à jour de la liste)
  useEffect(() => {
    if (socket) {
      const handleNewMessage = (message) => {
        setMessages(prev => [message, ...prev]);
      };

      socket.on('new-message', handleNewMessage);

      return () => {
        socket.off('new-message', handleNewMessage);
      };
    }
  }, [socket]);

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
    } catch (error) {
      console.error('Erreur chargement messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(res.data);
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedRecipient || !messageContent.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    if (messageContent.length > 140) {
      toast.error('Message trop long (max 140 caracteres)');
      return;
    }

    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/messages`,
        {
          recipientEmail: selectedRecipient,
          content: messageContent.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Message envoye');
      setShowNewMessage(false);
      setSelectedRecipient('');
      setMessageContent('');
      loadMessages();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur envoi message');
    } finally {
      setSending(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await axios.post(
        `${API_URL}/api/messages/${messageId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev =>
        prev.map(m => m.id === messageId ? { ...m, read_at: new Date().toISOString() } : m)
      );
    } catch (error) {
      console.error('Erreur marquage lu:', error);
    }
  };

  const handleDeleteMessage = async (messageId, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer ce message ?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/messages/${messageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message supprime');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur suppression');
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "A l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  const closeModal = () => {
    setShowNewMessage(false);
    setSelectedRecipient('');
    setMessageContent('');
  };

  return (
    <FocusableSection focusKey="MESSAGES_PAGE">
      <Container $sidebar={sidebar}>
        <Header>
          <Title>Messages</Title>
          <NewMessageButtonWrapper>
            <FocusableButton
              as={NewMessageButton}
              onPress={() => setShowNewMessage(true)}
            >
              <FaIcons.FaPlus /> Nouveau message
            </FocusableButton>
          </NewMessageButtonWrapper>
        </Header>

        {loading ? (
          <EmptyState>Chargement...</EmptyState>
        ) : messages.length === 0 ? (
          <EmptyState>
            <FaIcons.FaEnvelopeOpen style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }} />
            <p>Aucun message</p>
            <p style={{ fontSize: '0.9rem' }}>Envoyez votre premier message !</p>
          </EmptyState>
        ) : (
          <MessagesGrid>
            {messages.map(message => {
              const isSent = message.sender_email === user.email;
              const isUnread = !isSent && !message.read_at;
              const isRead = isSent && message.read_at;
              const contactName = isSent
                ? `${message.recipient_prenom} ${message.recipient_nom}`
                : `${message.sender_prenom} ${message.sender_nom}`;

              return (
                <FocusableCard
                  key={message.id}
                  as={MessageCard}
                  $sent={isSent}
                  $unread={isUnread}
                  onPress={() => isUnread && handleMarkAsRead(message.id)}
                >
                  <MessageHeader>
                    <MessageFrom>
                      {contactName}
                      <MessageDirection $sent={isSent}>
                        {isSent ? '(envoye)' : '(recu)'}
                      </MessageDirection>
                      {isUnread && <UnreadBadge>Nouveau</UnreadBadge>}
                    </MessageFrom>
                    <MessageTime>{formatTime(message.created_at)}</MessageTime>
                  </MessageHeader>
                  <MessageContent>{message.content}</MessageContent>
                  {isSent && (
                    <MessageFooter>
                      {isRead ? (
                        <ReadBadge>
                          <FaIcons.FaCheck /> Lu
                        </ReadBadge>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: '#888' }}>Non lu</span>
                      )}
                      <DeleteButton onClick={(e) => handleDeleteMessage(message.id, e)}>
                        <FaIcons.FaTrash /> Supprimer
                      </DeleteButton>
                    </MessageFooter>
                  )}
                </FocusableCard>
              );
            })}
          </MessagesGrid>
        )}

        {showNewMessage && (
          <Modal onClick={closeModal}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalTitle>Nouveau message</ModalTitle>

              <FormGroup>
                <Label>Destinataire</Label>
                <Select
                  value={selectedRecipient}
                  onChange={e => setSelectedRecipient(e.target.value)}
                >
                  <option value="">Choisir un contact...</option>
                  {contacts.map(contact => (
                    <option key={contact.id} value={contact.email}>
                      {contact.prenom} {contact.nom}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Message</Label>
                <TextArea
                  value={messageContent}
                  onChange={e => setMessageContent(e.target.value)}
                  placeholder="Votre message (max 140 caracteres)..."
                  maxLength={150}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey && !sending) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <CharCount $over={messageContent.length > 140}>
                  {messageContent.length}/140
                </CharCount>
              </FormGroup>

              <ModalButtons>
                <ModalButton className="secondary" onClick={closeModal}>
                  Annuler
                </ModalButton>
                <ModalButton
                  className="primary"
                  onClick={handleSendMessage}
                  disabled={sending || !selectedRecipient || !messageContent.trim() || messageContent.length > 140}
                >
                  {sending ? 'Envoi...' : 'Envoyer'}
                </ModalButton>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}
      </Container>
    </FocusableSection>
  );
};

export default Messages;
